import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Loader2, CheckCircle, Eye, EyeOff } from "lucide-react";
import paynovusLogo from "@/assets/paynovus-logo-horizontal-tagline.png";

const passwordSchema = z
  .string()
  .min(10, "Password must be at least 10 characters")
  .max(72, "Password is too long")
  .regex(/[A-Z]/, "Must contain at least one uppercase letter")
  .regex(/[0-9]/, "Must contain at least one number")
  .regex(/[^A-Za-z0-9]/, "Must contain at least one special character");

/**
 * AdminSetPassword handles three token delivery mechanisms:
 *
 * A) token_hash query param  →  verifyOtp({ token_hash, type })
 *    Used by Supabase v2 PKCE flows and newer email templates.
 *
 * B) access_token hash fragment  →  setSession({ access_token, refresh_token })
 *    Used by older implicit-flow email links.
 *
 * C) No token in URL  →  listen for PASSWORD_RECOVERY / SIGNED_IN events
 *    Supabase fires these when the user clicks a reset/invite link that
 *    redirects to the site root first, and the JS client exchanges the
 *    token automatically.
 *
 * The page also handles a plain "already signed in" state (page reload).
 */
export default function AdminSetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isReady, setIsReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [isInvite, setIsInvite] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Guard: only resolve once
  const readyRef = useRef(false);

  const markReady = (session: boolean, invite: boolean) => {
    if (!readyRef.current) {
      readyRef.current = true;
      setHasSession(session);
      setIsInvite(invite);
      setIsReady(true);
    }
  };

  useEffect(() => {
    let mounted = true;

    // ── Read token sources ──────────────────────────────────────
    // Source 1: Query params (Supabase v2 PKCE / token_hash)
    const tokenHash = searchParams.get("token_hash");
    const typeParam = searchParams.get("type") as "invite" | "recovery" | null;

    // Source 2: Hash fragment (implicit flow / older email links)
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    const typeFromHash = hashParams.get("type") as "invite" | "recovery" | null;

    // ── onAuthStateChange fires for ALL token delivery paths ────
    // Including path C (no URL params — Supabase client handled it)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY") {
        markReady(!!session, false);
      } else if (event === "SIGNED_IN") {
        const invite = typeParam === "invite" || typeFromHash === "invite";
        markReady(!!session, invite);
      }
    });

    const bootstrap = async () => {
      // Path A: token_hash in query string
      if (tokenHash && typeParam) {
        const { error: verifyErr } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: typeParam,
        });
        if (verifyErr && mounted) {
          setError("Your link has expired or was already used. Please request a new one.");
          markReady(false, false);
        }
        // onAuthStateChange (SIGNED_IN / PASSWORD_RECOVERY) will call markReady
        return;
      }

      // Path B: Hash fragment tokens
      if (accessToken && refreshToken) {
        const { error: sessionErr } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (sessionErr && mounted) {
          setError("Your link has expired or was already used. Please request a new one.");
          markReady(false, false);
        }
        // onAuthStateChange (SIGNED_IN / PASSWORD_RECOVERY) will call markReady
        return;
      }

      // Path C: No token — check for existing / already-exchanged session
      // Give onAuthStateChange a moment to fire for in-flight recovery events
      await new Promise(r => setTimeout(r, 800));
      if (readyRef.current) return; // already resolved by listener

      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      markReady(!!data.session, false);
    };

    bootstrap();

    // Safety net: never leave the user on a blank spinner
    const timeout = setTimeout(() => {
      if (!readyRef.current && mounted) {
        supabase.auth.getSession().then(({ data }) => {
          if (!readyRef.current && mounted) {
            markReady(!!data.session, false);
          }
        });
      }
    }, 5000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasSession) {
      setError(
        "Your link has expired or was already used. Please go back to login and request a new one."
      );
      return;
    }

    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid password");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");

      // Use the edge function so AAL2 / MFA requirement is bypassed via service role
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-password-reset`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ mode: "update_password", password: parsed.data }),
        }
      );
      const result = await res.json();
      if (!res.ok || result.error) {
        setError(result.error || "Could not update password. Please try again.");
        return;
      }

      setDone(true);

      // Re-fetch session to check MFA enrolment state
      const { data: refreshed } = await supabase.auth.getSession();
      const hasTOTP = refreshed.session?.user?.factors?.some(
        (f) => f.status === "verified" && f.factor_type === "totp"
      );

      await new Promise((r) => setTimeout(r, 1200));

      if (isInvite || !hasTOTP) {
        navigate("/admin/mfa-setup", { replace: true });
      } else {
        // Recovery flow — sign out so they log in fresh with MFA
        await supabase.auth.signOut();
        navigate("/admin/login", { replace: true });
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isReady) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">Verifying your link…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={paynovusLogo} alt="PayNovus" className="h-12" />
          </div>
          <div className="flex items-center justify-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl">
              {isInvite ? "Welcome to the Admin Portal" : "Reset your password"}
            </CardTitle>
          </div>
          <CardDescription>
            {!hasSession
              ? "This link has expired or was already used."
              : isInvite
              ? "Set a strong password to secure your account. You'll be asked to set up two-factor authentication next."
              : "Choose a new password for your admin account."}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {done ? (
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle className="h-10 w-10 text-primary" />
              <p className="font-medium">Password set successfully</p>
              <p className="text-sm text-muted-foreground">
                Redirecting you to{" "}
                {isInvite || !hasSession ? "MFA setup" : "login"}…
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {!hasSession && (
                <Alert>
                  <AlertDescription>
                    This link has expired or was already used.{" "}
                    <button
                      type="button"
                      className="underline font-medium"
                      onClick={() => navigate("/admin/login")}
                    >
                      Go back to login
                    </button>{" "}
                    and use "Forgot password?" to get a new link.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={isSaving || !hasSession}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword((p) => !p)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Min. 10 characters — uppercase, number and special character required.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm password</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirm ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={isSaving || !hasSession}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirm((p) => !p)}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={isSaving || !hasSession}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : isInvite ? (
                  "Set password & continue to MFA"
                ) : (
                  "Update password"
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate("/admin/login")}
              >
                Back to login
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
